import { type FC } from 'react';
import cn from 'classnames';
import { toast } from 'react-toastify';
import { type ErrorBoundaryScope } from '../../services/bugReport/collectErrorData';
import { copyToClipboard } from '../../utils/copyToClipboard';
import DangerTriangleIcon from '../../assets/icon/components/DangerTriangleIcon';
import styles from './errorBoundary.module.scss';

interface ErrorFallbackProps {
  scope: ErrorBoundaryScope;
  reportId: string | null;
  onReset: () => void;
}

export const ErrorFallback: FC<ErrorFallbackProps> = ({
  scope,
  reportId,
  onReset
}) => {
  const goHome = (): void => {
    window.location.assign('/');
  };

  const handleCopyReportId = async (): Promise<void> => {
    if (reportId === null) {
      return;
    }

    const copied = await copyToClipboard(reportId);

    if (copied) {
      toast.success('Код ошибки скопирован');
      return;
    }

    toast.error('Не удалось скопировать код ошибки');
  };

  return (
    <div
      className={cn(styles['error-fallback'], {
        [styles['error-fallback--global']]: scope === 'global',
        [styles['error-fallback--page']]: scope === 'page'
      })}
      role="alert"
      aria-live="assertive"
    >
      <div className={styles['error-fallback__content']}>
        <h1 className={styles['error-fallback__title']}>Что-то пошло не так</h1>

        <div className={styles['error-fallback__gif-wrapper']}>
          <img
            className={styles['error-fallback__gif']}
            src="/gif/dribbble_1.gif"
            alt="Иллюстрация ошибки"
            loading="lazy"
          />
        </div>

        <div className={styles['error-fallback__notice']}>
          <span className={styles['error-fallback__icon']} aria-hidden="true">
            <DangerTriangleIcon
              className={styles['error-fallback__icon-svg']}
              color="#dc2626"
            />
          </span>
          <p className={styles['error-fallback__description']}>
            Произошла ошибка в админ-панели. Пожалуйста, сообщите об этом
            команде разработки и передайте код ошибки ниже.
          </p>
        </div>

        {reportId !== null ? (
          <p className={styles['error-fallback__report']}>
            Код ошибки:{' '}
            <button
              type="button"
              className={styles['error-fallback__report-code']}
              onClick={handleCopyReportId}
              aria-label="Скопировать код ошибки"
            >
              {reportId}
            </button>
          </p>
        ) : (
          <p
            className={cn(
              styles['error-fallback__report'],
              styles['error-fallback__report--muted']
            )}
          >
            Отчёт об ошибке зафиксирован. Сообщите команде разработки, на каком
            экране и при каком действии это произошло.
          </p>
        )}

        <div className={styles['error-fallback__actions']}>
          <button
            type="button"
            className={cn(
              styles['error-fallback__button'],
              styles['error-fallback__button--primary']
            )}
            onClick={onReset}
          >
            Попробовать снова
          </button>
          <button
            type="button"
            className={cn(
              styles['error-fallback__button'],
              styles['error-fallback__button--secondary']
            )}
            onClick={goHome}
          >
            На главную
          </button>
        </div>
      </div>
    </div>
  );
};
